import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Save } from 'lucide-react';

interface Work4MeSkillFormProps {
  name: string;
  description: string;
  body: string;
  isEnabled: boolean;
  isDirty: boolean;
  isSaving: boolean;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  onIsEnabledChange: (value: boolean) => void;
  onSave: () => void;
  submitLabel?: string;
  submittingLabel?: string;
  editableHint?: string;
}

export function Work4MeSkillForm({
  name,
  description,
  body,
  isEnabled,
  isDirty,
  isSaving,
  onNameChange,
  onDescriptionChange,
  onBodyChange,
  onIsEnabledChange,
  onSave,
  submitLabel = 'Save Skill',
  submittingLabel = 'Saving...',
  editableHint = 'Changes are applied immediately when saved.',
}: Work4MeSkillFormProps) {
  const canSave = name.trim().length > 0 && body.trim().length > 0 && !isSaving;

  return (
    <>
      <div className="grid gap-4 px-4 pt-4">
        <div>
          <Label htmlFor="skill-name">Name</Label>
          <Input
            id="skill-name"
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="skill-description">Description</Label>
          <Textarea
            id="skill-description"
            rows={3}
            value={description}
            onChange={(event) => onDescriptionChange(event.target.value)}
            className="mt-1 resize-none"
          />
        </div>

        <div>
          <Label htmlFor="skill-body">Body</Label>
          <Textarea
            id="skill-body"
            rows={12}
            value={body}
            onChange={(event) => onBodyChange(event.target.value)}
            className="mt-1"
          />
        </div>

        <div className="flex items-center justify-between rounded border p-3">
          <div>
            <p className="text-sm font-medium">Enabled</p>
            <p className="text-xs text-muted-foreground">Disabled skills stay stored but are excluded at runtime.</p>
          </div>
          <Switch checked={isEnabled} onCheckedChange={onIsEnabledChange} />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 px-4 pt-3 pb-2">
        <p className="text-xs text-muted-foreground">{editableHint}</p>
        <Button
          onClick={onSave}
          className="gap-2"
          disabled={!canSave || !isDirty}
        >
          <Save className="h-4 w-4" />
          {isSaving ? submittingLabel : submitLabel}
        </Button>
      </div>
    </>
  );
}
